import { useEffect, useState } from 'react';
import { useJobTaskApi } from '../../../clients/quipli-service/job-tasks/job-task-api';
import { JobConfig, Task, TaskConfig, TaskType } from '../../../clients/quipli-service/job-tasks/job-tasks';
import { Typography } from '../../../components/typography/typography';
import { SelectForm } from '../../../components/select-form/select-form';
import { toast } from '../../../util/toasts';
import { Spinner } from '../../../components/spinner/spinner';
import Icon from '../../../components/icons/icon';
import { EditJobConfig } from './edit-job-config';
import './job-configs.scss';
import { Button } from '../../../components/form/button';
import { Product, ProductInventoryUnit } from '../../../clients/quipli-service/product/product';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { Dictionary, keyBy } from 'lodash';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { InspectionChecklist } from '../inspection/inspection-modal/inspection-checklist';
import { MachineHoursTask } from '../inspection/inspection-modal/machine-hours-task';
import { MediaUploadTask } from '../inspection/inspection-modal/media-upload-task';

// TODO: wtf am i calling these really
export const JobConfigs = () => {
  const { listJobConfigs, deleteJobConfig } = useJobTaskApi();
  const { getMultipleConfigurations } = useAPI();
  const [jobConfigs, setJobConfigs] = useState<JobConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobConfig, setSelectedJobConfig] = useState<JobConfig>();
  const [duplicateJobConfig, setDuplicateJobConfig] = useState<JobConfig>();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeletingOpen, setIsDeletingOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [configs, setConfigs] = useState<Dictionary<Configuration>>();

  useEffect(() => {
    setDuplicateJobConfig(
      selectedJobConfig ? { ...selectedJobConfig, name: `${selectedJobConfig.name ?? ''} (copy)` } : undefined,
    );
  }, [selectedJobConfig]);

  const init = async () => {
    try {
      const [jobConfigs, configs] = await Promise.all([
        listJobConfigs(),
        getMultipleConfigurations(['CONF_UNIT_TRACKING_VIA', 'CONF_FUEL_UNIT']),
      ]);
      setJobConfigs(jobConfigs);
      setSelectedJobConfig(jobConfigs[0]);
      setConfigs(keyBy(configs, 'variable'));
      setLoading(false);
    } catch (e) {
      console.error(e);
      toast('Failed to load job configs', 'error');
    }
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <div className="job-configs-container m-4">
      <div className="job-configs m-2">
        <div className="flex justify-between w-full mb-3">
          <Typography type="display-sm">Inspection Workflows</Typography>
          <Button
            className="button button-secondary flex gap-2 items-center"
            onClick={() => {
              setSelectedJobConfig(undefined);
              setIsEditing(true);
            }}
          >
            <Icon icon="Plus" />
            <span>Create New</span>
          </Button>
        </div>
        {loading || !jobConfigs ? (
          <Spinner />
        ) : !isEditing && jobConfigs.length ? (
          <>
            <SelectForm
              className="mt-4"
              options={jobConfigs}
              value={jobConfigs.find((jc) => jc.id === selectedJobConfig?.id)}
              getOptionLabel={(jc) => jc.name}
              onChange={(v) =>
                setSelectedJobConfig(
                  !v
                    ? undefined
                    : ({
                        ...v,
                        taskConfigs: orderTaskConfigs(v.taskConfigs),
                      } as JobConfig),
                )
              }
            />

            {selectedJobConfig?.taskConfigs?.length && configs && (
              <div className="job-config-view">
                <div className="flex gap-4 my-4">
                  <Typography type="text-lg" weight="medium">
                    Inspection Example
                  </Typography>
                  <Button
                    data-testid="inspection-edit"
                    className="button button-secondary h-4"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </Button>
                  <Button className="button button-secondary h-4" onClick={() => setIsDeletingOpen(true)}>
                    Delete
                  </Button>
                  <Button
                    data-testid="inspection-duplicate"
                    className="button button-secondary h-4"
                    onClick={() => setIsDuplicating(true)}
                  >
                    Duplicate Checklist
                  </Button>
                </div>
                <div className="flex flex-wrap bg-grey-100 p-4 rounded-md">
                  {selectedJobConfig.taskConfigs.map((tc) => {
                    return (
                      <div
                        key={tc.id}
                        className="task-config-preview max-w-[400px] border border-solid border-grey-200 rounded-md m-4 p-3 w-[400px] bg-white"
                      >
                        {tc.type === TaskType.InspectionChecklist ? (
                          <InspectionChecklist
                            task={
                              {
                                ...fakeInspectionTaskData,
                                header: tc.header,
                                instructions: tc.instructions,
                                metadata: tc.metadata,
                              } as Task
                            }
                            order={undefined}
                            jobId="123"
                            product={
                              {
                                id: 0,
                                name: 'Inspection Preview',
                                allowHourTracking: true,
                                fuelChargeProductId: 1,
                              } as Product
                            }
                            unit={{ id: 1, serialNumber: '123ABC', unitNumber: '25' } as ProductInventoryUnit}
                            unitTrackingVia={configs['CONF_UNIT_TRACKING_VIA']!.value as 'serialNumber' | 'unitNumber'}
                            next={() => 0}
                            back={() => 0}
                            totalTasks={3}
                            isPreview={true}
                            setInspection={() => 0}
                          />
                        ) : tc.type === TaskType.MachineHours ? (
                          <MachineHoursTask
                            task={
                              {
                                ...fakeInspectionTaskData,
                                header: tc.header,
                                instructions: tc.instructions,
                                metadata: tc.metadata,
                              } as Task
                            }
                            jobId="123"
                            jobType={selectedJobConfig.jobType}
                            product={
                              {
                                id: 0,
                                name: 'Inspection Preview',
                                allowHourTracking: true,
                                fuelChargeProductId: 1,
                              } as Product
                            }
                            unit={{ id: 1, serialNumber: '123ABC', unitNumber: '25' } as ProductInventoryUnit}
                            unitTrackingVia={configs['CONF_UNIT_TRACKING_VIA']!.value as 'serialNumber' | 'unitNumber'}
                            fuelUnit={configs['CONF_FUEL_UNIT']!.value as 'gallons' | 'liters'}
                            next={() => 0}
                            back={() => 0}
                            totalTasks={3}
                            isPreview={true}
                            setInspection={() => 0}
                          />
                        ) : (
                          <MediaUploadTask
                            task={
                              {
                                ...fakeInspectionTaskData,
                                header: tc.header,
                                instructions: tc.instructions,
                                metadata: tc.metadata,
                              } as Task
                            }
                            jobId="123"
                            product={
                              {
                                id: 0,
                                name: 'Inspection Preview',
                                allowHourTracking: true,
                                fuelChargeProductId: 1,
                              } as Product
                            }
                            unit={{ id: 1, serialNumber: '123ABC', unitNumber: '25' } as ProductInventoryUnit}
                            unitTrackingVia={configs['CONF_UNIT_TRACKING_VIA']!.value as 'serialNumber' | 'unitNumber'}
                            next={() => 0}
                            back={() => 0}
                            totalTasks={3}
                            isPreview={true}
                            order={undefined}
                            setInspection={() => 0}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : isEditing ? (
          <EditJobConfig
            jobConfig={selectedJobConfig ?? null}
            close={(updated?: JobConfig) => {
              setIsEditing(false);
              init().then(() => setSelectedJobConfig(updated));
            }}
          />
        ) : (
          <Typography type="text-md">No job configs found</Typography>
        )}

        <ConfirmModal
          isOpen={isDeletingOpen}
          onConfirm={async () => {
            try {
              if (!selectedJobConfig) {
                toast('No workflow selected', 'error');
                setIsDeletingOpen(false);
                return;
              }
              setIsDeleting(true);
              await deleteJobConfig(selectedJobConfig.id);
              toast('Successfully deleted inspection workflow', 'success');
              await init();
            } catch {
              toast('Failed to delete inspection workflow', 'error');
            } finally {
              setIsDeletingOpen(false);
              setIsDeleting(false);
            }
          }}
          headerIcon={<i className="fa fa-trash-can delete-icon"></i>}
          close={() => setIsDeletingOpen(false)}
          disableButtons={isDeleting}
          yesLabel="Delete"
          noLabel="Cancel"
          prompt={'Delete Inspection Workflow'}
          additionalText="Are you sure you want to delete this inspection workflow? This cannot be undone"
        />
        {isDuplicating && (
          <EditJobConfig
            jobConfig={duplicateJobConfig ?? null}
            isDuplicating={true}
            close={(updated?: JobConfig) => {
              if (updated) {
                init()
                  .then(() => setSelectedJobConfig(updated))
                  .then(() => {
                    setIsDuplicating(false);
                  });
              } else {
                setIsDuplicating(false);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export const orderTaskConfigs = (taskConfigs: TaskConfig[]) => {
  return [
    taskConfigs.find((tc) => tc.type == TaskType.InspectionChecklist)!,
    taskConfigs.find((tc) => tc.type == TaskType.MachineHours)!,
    taskConfigs.find((tc) => tc.type == TaskType.MediaUpload)!,
  ];
};

const fakeInspectionTaskData: Partial<Task> = {
  id: '1',
  jobId: '123',
  taskConfigId: '1',
  taskConfigVersion: 1,
  status: 'NotStarted',
};
